'use client';
/**
 * LE PRÉVISIONNEL, MOIS PAR MOIS (RQ, 01/10/2026).
 * ─────────────────────────────────────────────────────────────────────────
 * « Je veux voir vraiment qui tu choisis selon les règles. Je voudrais qu'on
 * réfléchisse au prévisionnel mois par mois. Le planning ne comporte alors
 * que les absences prévues ou demandées. Ensuite le prévisionnel remplit les
 * cases du mois en tenant compte des règles. »
 *
 * Deux lectures, comme le planning en cours : PAR PERSONNE (lignes =
 * personnes) et PAR POSTE (lignes = postes). Mêmes couleurs que l'en cours —
 * couleur du poste en vue personne, de la personne en vue poste — mais SANS
 * le fond coloré : on ne doit pas oublier qu'on est dans un projet.
 * Un clic sur une case dit POURQUOI la personne a été choisie (raisons du
 * générateur lui-même) et permet de l'écarter avant la validation.
 * En vue personne, les décomptes O / G / D-F du mois prévu, puis des douze
 * derniers mois (planning réel).
 */
import React from 'react';
import { abregePoste } from '@/lib/abrege-poste';
import { nomDe, prenomDe } from '@/lib/nom';
import { pastilleDe, initialesSansCollision } from '@/lib/couleurs-personne';
import { FAMILLES_PERSONNEL as FAMILLES, familleDe, sansTitre } from '@/lib/familles-personnel';
import type { RhAbsence, RhPersonnel, RhPoste } from '@/lib/types';
import { I } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { isoJour } from '@/lib/meta';
import { enTeteCollant } from '@/shared/Tableau';
export type ProjetCase = {
  date: string; poste: string; posteLibelle: string; orgId: string; nom: string;
  derogation?: boolean; raisons?: string[];
  /** En formation, en doublon du titulaire (règle de formation tournante). */
  formation?: boolean;
};
/** Une affectation réelle, réduite à ce qu'il faut pour compter. */
export type TenueReelle = { date: string; orgId: string; poste: string; heureDebut?: string | null; heureFin?: string | null };

const JOURS_COURTS = ['di', 'lu', 'ma', 'me', 'je', 've', 'sa'];

/* L'IMPRESSION A4 PAYSAGE (RQ, 01/10/2026 : « qu'il tienne comme celui en
   cours dans une page A4 ») — les mêmes partis que la vue Mois : taille réelle
   en POINTS, colonnes égales, couleurs forcées. `p` préfixe les sélecteurs. */
const IMPRESSION = (p: string) => `
  @page { size: A4 landscape; margin: 5mm; }
  body * { visibility: hidden; }
  ${p}.prev-impr, ${p}.prev-impr * { visibility: visible; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  ${p}.prev-impr { position: absolute; left: 0; top: 0; width: 100%; }
  ${p}.prev-impr button, ${p}.prev-impr .prev-ecran { display: none !important; }
  ${p}.prev-impr .prev-defile { overflow: visible !important; border: none !important; }
  ${p}.prev-impr table { min-width: 0 !important; width: 100% !important; table-layout: fixed !important; }
  ${p}.prev-impr col.prev-col-tete { width: 34mm !important; }
  ${p}.prev-impr col.prev-col-tot { width: 6mm !important; }
  ${p}.prev-impr th, ${p}.prev-impr td { position: static !important; height: auto !important; padding: 0.2mm !important; border-color: var(--border-default) !important; font-size: 6.5pt !important; }
  ${p}.prev-impr .prev-etiq { font-size: 6.5pt !important; line-height: 1.05 !important; padding: 0 0.3mm !important; margin: 0 !important; }
  ${p}.prev-impr tr { break-inside: avoid; }
  ${p}.prev-impr .prev-titre { display: block !important; font-size: 10pt !important; margin: 0 0 1mm !important; }
  ${p}.prev-impr .prev-legende { font-size: 6.5pt !important; margin-top: 1mm !important; }
`;
const CAPTURE = IMPRESSION('').split('\n').filter((l) => !/@page|body \*|visibility|position: absolute/.test(l)).join('\n')
  .replace(/\.prev-impr/g, '.prev-impr.prev-capture');


/** O / G / D-F en VACATIONS, comme la vue Mois de l'en cours : deux postes sur
 *  la même plage = une vacation ; la garde se compte à part. */
function compter(l: TenueReelle[], chomes: Set<string>) {
  const par = new Map<string, Map<string, Set<string>>>();
  const gardes = new Map<string, number>();
  for (const t of l) {
    if (t.poste === 'GARDE') { gardes.set(t.orgId, (gardes.get(t.orgId) || 0) + 1); continue; }
    const m = par.get(t.orgId) || par.set(t.orgId, new Map()).get(t.orgId)!;
    (m.get(t.date) || m.set(t.date, new Set()).get(t.date)!).add(`${t.heureDebut || ''}-${t.heureFin || ''}`);
  }
  const out = new Map<string, { o: number; g: number; df: number }>();
  const ids = new Set([...par.keys(), ...gardes.keys()]);
  for (const id of ids) {
    let o = 0; let df = 0;
    for (const [d, s] of par.get(id) || []) {
      if (chomes.has(d) || new Date(`${d}T12:00:00Z`).getUTCDay() === 0) df += s.size; else o += s.size;
    }
    out.set(id, { o, g: gardes.get(id) || 0, df });
  }
  return out;
}

export function PrevisionnelMois({ du, au, personnes, postes, absences, projet, vides, ecartees, onBasculer, dejaPlanifie, historique12, chomes, titre = '', veille = [] }: {
  /** Ce qui est tenu la VEILLE du premier jour (hors du mois) — sert au seul post garde du 1er. */
  veille?: { orgId: string; poste: string }[];
  du: string; au: string;
  /** Le titre imprimé (« Prévisionnel — novembre 2026 »). */
  titre?: string;
  personnes: RhPersonnel[];
  postes: RhPoste[];
  absences: RhAbsence[];
  projet: ProjetCase[] | null;
  vides: { date: string; poste: string; motif: string }[];
  ecartees: Set<string>;
  onBasculer: (cles: string[]) => void;
  /** Affectations déjà au planning sur le mois (hors génération). */
  dejaPlanifie: TenueReelle[];
  /** Les dimanches et fériés des trois mois du trimestre (réel + prévu). */
  historique12: Map<string, number> | null;
  /** Jours chômés (fériés) connus. */
  chomes: Set<string>;
}) {
  const [vue, setVue] = React.useState<'personne' | 'poste'>('personne');
  /* LES INITIALES, comme l'en cours (RQ, 01/10/2026 : « aussi compact »). */
  const initiales = React.useMemo(() => {
    const prises = new Set(personnes.map((p) => String(p.initiales || '').trim().toUpperCase()).filter(Boolean));
    const par = new Map<string, string>();
    for (const p of personnes) {
      const saisie = String(p.initiales || '').trim().toUpperCase();
      if (saisie) { par.set(p.orgId, saisie); continue; }
      const v = initialesSansCollision(p.nom || '', prises); prises.add(v); par.set(p.orgId, v);
    }
    return par;
  }, [personnes]);
  const boiteRef = React.useRef<HTMLDivElement>(null);
  const [pdfEnCours, setPdfEnCours] = React.useState(false);
  /* Le PDF A4 paysage, toujours à l'endroit (même procédé que la vue Mois). */
  const exporterPdf = async () => {
    const el = boiteRef.current;
    if (!el) return;
    setPdfEnCours(true);
    el.classList.add('prev-capture'); el.style.width = '1085px';
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const [{ toPng }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
      const png = await toPng(el, {
        pixelRatio: 3, backgroundColor: '#ffffff',
        filter: (n) => !(n instanceof HTMLElement && (n.tagName === 'BUTTON' || n.classList.contains('prev-ecran'))),
      });
      const img = new Image(); img.src = png; await img.decode();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const r = Math.min(287 / img.width, 200 / img.height);
      doc.addImage(png, 'PNG', 5, 5, img.width * r, img.height * r);
      doc.save(`previsionnel-${du.slice(0, 7)}-${vue}.pdf`);
    } finally {
      el.classList.remove('prev-capture'); el.style.width = ''; setPdfEnCours(false);
    }
  };
  const [choisie, setChoisie] = React.useState<{ orgId: string; date: string } | null>(null);
  const dates = React.useMemo(() => {
    const l: string[] = [];
    for (let d = new Date(`${du}T12:00:00Z`); isoJour(d) <= au; d.setUTCDate(d.getUTCDate() + 1)) l.push(isoJour(d));
    return l;
  }, [du, au]);
  const posteDe = React.useMemo(() => new Map(postes.map((p) => [p.code, p])), [postes]);
  const couleurPers = React.useMemo(() => new Map(personnes.map((p) => [p.orgId, p.couleur || null])), [personnes]);
  const absenceDe = (orgId: string, date: string) => absences.find((a) => a.personnel?.orgId === orgId
    && (a.statut === 'validee' || a.statut === 'demandee') && a.dateDebut <= date && date <= a.dateFin);
  const parCase = React.useMemo(() => {
    const m = new Map<string, ProjetCase[]>();
    for (const x of projet || []) { const k = `${x.orgId}|${x.date}`; (m.get(k) || m.set(k, []).get(k)!).push(x); }
    return m;
  }, [projet]);
  const parPosteJour = React.useMemo(() => {
    const m = new Map<string, ProjetCase[]>();
    for (const x of projet || []) { const k = `${x.poste}|${x.date}`; (m.get(k) || m.set(k, []).get(k)!).push(x); }
    return m;
  }, [projet]);
  const dejaPar = React.useMemo(() => {
    const m = new Map<string, string[]>();
    for (const x of dejaPlanifie) { const k = `${x.orgId}|${x.date}`; (m.get(k) || m.set(k, []).get(k)!).push(x.poste); }
    return m;
  }, [dejaPlanifie]);
  /* POST GARDE (RQ, 03/10/2026) : le lendemain d'une garde est le repos de garde.
     Sans poste ce jour-là, la case le DIT au lieu de rester blanche — une case
     blanche se lit « libre », et on y poserait quelqu'un qui sort de sa nuit. */
  const postGarde = (orgId: string, date: string) => {
    const x = new Date(`${date}T12:00:00Z`); x.setUTCDate(x.getUTCDate() - 1);
    const jVeille = isoJour(x);
    if (jVeille < du) return veille.some((v) => v.orgId === orgId && posteDe.get(v.poste)?.categorie === 'garde');
    return (parCase.get(`${orgId}|${jVeille}`) || []).some((c) => !ecartees.has(cle(c)) && posteDe.get(c.poste)?.categorie === 'garde')
      || (dejaPar.get(`${orgId}|${jVeille}`) || []).some((code) => posteDe.get(code)?.categorie === 'garde');
  };
  const videsPar = React.useMemo(() => new Set(vides.map((v) => `${v.poste}|${v.date}`)), [vides]);
  const videsParJour = React.useMemo(() => {
    const m = new Map<string, string[]>();
    for (const v of vides) (m.get(v.date) || m.set(v.date, []).get(v.date)!).push(v.poste);
    return m;
  }, [vides]);
  /* Les décomptes : le mois prévu (projet + déjà planifié), puis 12 mois réels. */
  const cptPrevu = React.useMemo(() => compter([
    ...dejaPlanifie,
    ...(projet || []).filter((x) => !ecartees.has(`${x.date}|${x.poste}|${x.orgId}`))
      .map((x) => ({ date: x.date, orgId: x.orgId, poste: x.poste, heureDebut: posteDe.get(x.poste)?.plageDebut, heureFin: posteDe.get(x.poste)?.plageFin })),
  ], chomes), [dejaPlanifie, projet, ecartees, posteDe, chomes]);
  const cpt12 = React.useMemo(() => (historique12 ? new Map([...historique12].map(([o, df]) => [o, { o: 0, g: 0, df }])) : null), [historique12]);
  /* LE FOND DES D/F SELON LE NOMBRE (RQ, 01/10/2026) : du blanc (aucun) au
     rouge (le plus chargé) — qui porte le plus de dimanches se voit d'un coup. */
  const maxPrevuDF = Math.max(1, ...personnes.map((p) => cptPrevu.get(p.orgId)?.df || 0));
  const max12DF = Math.max(1, ...personnes.map((p) => cpt12?.get(p.orgId)?.df || 0));
  const fondDF = (n: number, max: number) => (n ? `hsl(${Math.round(55 - 55 * (n / max))} 85% ${Math.round(92 - 22 * (n / max))}%)` : undefined);
  const lignesPostes = React.useMemo(() => {
    const codes = new Set<string>([...(projet || []).map((x) => x.poste), ...vides.map((v) => v.poste)]);
    return postes.filter((p) => codes.has(p.code)).sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999) || a.code.localeCompare(b.code));
  }, [projet, vides, postes]);

  const cle = (x: ProjetCase) => `${x.date}|${x.poste}|${x.orgId}`;
  const groupes = React.useMemo(() => FAMILLES.map((f) => ({ cle: f.cle, titre: f.titre }))
    .map((g) => ({ ...g, personnes: personnes.filter((p) => familleDe(p) === g.cle).sort((a, b) => sansTitre(nomDe(a)).localeCompare(sansTitre(nomDe(b)), 'fr')) }))
    .filter((g) => g.personnes.length), [personnes]);
  /* La colonne du DIMANCHE (et des fériés) en jaune, de haut en bas (RQ, 01/10/2026). */
  const JAUNE = 'var(--amber-100)';
  const fondJour = (d: string) => (new Date(`${d}T12:00:00Z`).getUTCDay() === 0 || chomes.has(d) ? JAUNE : undefined);
  const td: React.CSSProperties = { border: '1px solid var(--border-subtle)', padding: '0 1px', fontSize: 'var(--tableau-ligne-compacte-police)', textAlign: 'center', height: 22, verticalAlign: 'middle' };
  const tdTot: React.CSSProperties = { ...td, width: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums' };
  /* LES TOTAUX COLLÉS À DROITE (RQ, 03/10/2026 : « je ne vois plus le total des
     dimanches du mois et du trimestre ») : un mois de 30 jours dépasse la
     largeur du cadre, et les trois colonnes partaient derrière le défilement.
     Collées au bord droit, elles restent lisibles ; un fond OPAQUE est requis,
     sinon les jours défilent au travers. Ordre : O · D/F · D/F trim. */
  const colle = (i: 0 | 1 | 2, fond?: string): React.CSSProperties => ({
    position: 'sticky', insetInlineEnd: [62, 36, 0][i], zIndex: 1, background: fond || 'var(--surface-card)',
    ...(i === 0 ? { boxShadow: '-2px 0 0 var(--border-default)' } : {}),
  });
  const sel = choisie ? (parCase.get(`${choisie.orgId}|${choisie.date}`) || []) : [];
  const selPers = choisie ? personnes.find((p) => p.orgId === choisie.orgId) : undefined;
  /* Une étiquette « sans fond » : la couleur de l'en cours sur le texte et le
     cadre, fond blanc — on reconnaît, sans confondre avec le réel. */
  const etiquette = (texte: string, cleCouleur: string, couleur: string | null, barre = false) => {
    const p = pastilleDe(cleCouleur, couleur);
    return (
      <span className="prev-etiq" style={{ display: 'inline-block', color: p.color, border: `1px solid ${p.borderColor}`, background: 'var(--surface-card)', borderRadius: 'var(--radius-xs)',
        fontSize: 9, fontWeight: 700, lineHeight: '13px', padding: '0 1px', margin: '0 1px', maxWidth: '100%', overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: barre ? 0.35 : 1, textDecoration: barre ? 'line-through' : undefined }}>
        {texte}
      </span>
    );
  };
  const entete = (
    <tr>
      <th style={{ ...td, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)', zIndex: 2 }}>{vue === 'personne' ? 'Personne' : 'Poste'}</th>
      {dates.map((d) => {
        const j = new Date(`${d}T12:00:00Z`).getUTCDay();
        return (
          <th key={d} style={{ ...td, background: fondJour(d) || 'var(--gray-25)', fontWeight: 600 }}>
            <div style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>{JOURS_COURTS[j]}</div>{d.slice(8)}
          </th>
        );
      })}
      {vue === 'personne' && (
        <>
          {/* 01/10/2026 : sans les gardes de nuit, et sans les ouvrés des 12 mois. */}
          {(['O', 'D/F'] as const).map((k, i) => <th key={`p${k}`} title="Le mois prévu" style={{ ...tdTot, ...colle(i as 0 | 1, 'var(--blue-50)') }}>{k}</th>)}
          <th title="Dimanches et fériés des TROIS MOIS du trimestre : planning réel + prévisionnel (garde de nuit non comptée)" style={{ ...tdTot, width: 36, ...colle(2, 'var(--gray-25)') }}>D/F trim.</th>
        </>
      )}
    </tr>
  );

  return (
    <div ref={boiteRef} className="prev-impr">
      <style>{`@media print { ${IMPRESSION('')} } ${CAPTURE}`}</style>
      <strong className="prev-titre" style={{ display: 'none' }}>{titre || 'Prévisionnel'} — projet, non validé · par {vue}</strong>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <Segment ariaLabel="Vue" valeur={vue} onChange={setVue} options={(['personne', 'poste'] as const).map((v) => ({ valeur: v, libelle: <>Par {v}</> }))} />
        {vue === 'personne' && (
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            Décomptes en vacations : <strong style={{ color: 'var(--text-body)' }}>fond bleu = le mois prévu</strong>, puis gris = les trois mois du trimestre (réel + prévu) · O ouvrés · G gardes · D/F dimanches et fériés · <strong style={{ color: 'var(--navy-700)' }}>PG</strong> post garde (repos après la garde de la veille).
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Bouton variante="primaire" taille="sm" type="button" disabled={pdfEnCours} onClick={() => void exporterPdf()} title="Un PDF A4 paysage, toujours à l'endroit">
          <I n="download" s={13} /> {pdfEnCours ? 'PDF…' : 'PDF (A4 paysage)'}
        </Bouton>
        <Bouton taille="sm" type="button" onClick={() => window.print()}><I n="print" s={13} /> Imprimer</Bouton>
      </div>
      <div className="prev-defile" style={{ overflowX: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
        <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%', minWidth: 120 + dates.length * 28 + (vue === 'personne' ? 90 : 0) }}>
          <colgroup>
            <col className="prev-col-tete" style={{ width: 120 }} />{dates.map((d) => <col key={d} />)}
            {vue === 'personne' && [0, 1, 2].map((i) => <col key={i} className="prev-col-tot" style={{ width: i === 2 ? 36 : 26 }} />)}
          </colgroup>
          <thead>{entete}</thead>
          <tbody>
            {vue === 'personne' ? groupes.flatMap((g) => [(
              <tr key={`g-${g.cle}`}>
                <th scope="row" colSpan={dates.length + 4} style={{ ...td, textAlign: 'start', background: 'var(--gray-100)', fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, position: 'sticky', insetInlineStart: 0 }}>
                  {g.titre}
                </th>
              </tr>
            ), ...g.personnes.map((p) => {
              const a = cptPrevu.get(p.orgId) || { o: 0, g: 0, df: 0 };
              const b = cpt12?.get(p.orgId) || { o: 0, g: 0, df: 0 };
              return (
                <tr key={p.orgId}>
                  <th scope="row" title={nomDe(p)} style={{ ...td, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)', fontWeight: 600, fontSize: 'var(--tableau-entete-compacte-police)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nomDe(p)}
                  </th>
                  {dates.map((d) => {
                    const abs = absenceDe(p.orgId, d);
                    const cases = parCase.get(`${p.orgId}|${d}`) || [];
                    const deja = dejaPar.get(`${p.orgId}|${d}`) || [];
                    const estChoisie = choisie?.orgId === p.orgId && choisie.date === d;
                    return (
                      <td key={d}
                        role={cases.length ? 'button' : undefined} tabIndex={cases.length ? 0 : undefined}
                        onClick={() => cases.length && setChoisie({ orgId: p.orgId, date: d })}
                        onKeyDown={(e) => { if (cases.length && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setChoisie({ orgId: p.orgId, date: d }); } }}
                        title={abs ? (abs.statut === 'validee' ? 'Absence validée' : 'Absence demandée')
                          : !cases.length && !deja.length && postGarde(p.orgId, d) ? 'Post garde — repos après la garde de la veille'
                          : cases.length ? `${cases.map((x) => x.posteLibelle).join(' + ')} — cliquez pour savoir pourquoi` : deja.length ? `Déjà au planning : ${deja.join(', ')}` : undefined}
                        style={{ ...td, background: fondJour(d), cursor: cases.length ? 'pointer' : 'default',
                          outline: estChoisie ? '2px solid var(--color-primary)' : undefined, outlineOffset: -2 }}>
                        {abs ? <span style={{ fontWeight: 700, color: abs.statut === 'validee' ? 'var(--red-600)' : 'var(--amber-600)' }}>{abs.statut === 'validee' ? 'Abs' : 'Dem'}</span>
                          : cases.length ? cases.map((x) => <React.Fragment key={cle(x)}>{etiquette(abregePoste(x.poste) + (x.derogation ? '*' : '') + (x.formation ? ' f' : ''), x.poste, null, ecartees.has(cle(x)))}</React.Fragment>)
                            : deja.length ? <span style={{ color: 'var(--text-muted)' }}>{deja.map(abregePoste).join('+')}</span>
                              : postGarde(p.orgId, d) ? <span style={{ fontWeight: 700, color: 'var(--navy-700)' }}>PG</span> : null}
                      </td>
                    );
                  })}
                  <td style={{ ...tdTot, ...colle(0, 'var(--blue-50)') }}>{a.o}</td>
                  <td style={{ ...tdTot, ...colle(1, fondDF(a.df, maxPrevuDF)) }}>{a.df}</td>
                  <td style={{ ...tdTot, width: 36, ...colle(2, cpt12 ? fondDF(b.df, max12DF) : undefined) }}>{cpt12 ? b.df : '…'}</td>
                </tr>
              );
            })]) : lignesPostes.map((po) => (
              <tr key={po.code}>
                <th scope="row" title={po.libelle} style={{ ...td, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)', fontWeight: 600, fontSize: 'var(--tableau-entete-compacte-police)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {po.libelle}
                </th>
                {dates.map((d) => {
                  const cases = parPosteJour.get(`${po.code}|${d}`) || [];
                  const vide = videsPar.has(`${po.code}|${d}`);
                  return (
                    <td key={d} title={vide ? 'Sans candidat — restera vide' : cases.map((x) => x.nom).join(', ') || undefined}
                      style={{ ...td, background: fondJour(d), cursor: cases.length ? 'pointer' : 'default' }}
                      onClick={() => cases[0] && setChoisie({ orgId: cases[0].orgId, date: d })}>
                      {cases.map((x) => {
                        const p = personnes.find((q) => q.orgId === x.orgId);
                        return <React.Fragment key={cle(x)}>{etiquette((initiales.get(x.orgId) || (p ? prenomDe(p) : x.nom)) + (x.derogation ? '*' : '') + (x.formation ? 'f' : ''), x.orgId, couleurPers.get(x.orgId) || null, ecartees.has(cle(x)))}</React.Fragment>;
                      })}
                      {vide && <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>vide</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            {projet && vue === 'personne' && (
              <tr>
                <th scope="row" style={{ ...td, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)', fontSize: 'var(--tableau-entete-compacte-police)' }}>Cases vides</th>
                {dates.map((d) => {
                  const v = videsParJour.get(d) || [];
                  return (
                    <td key={d} title={v.join(', ') || undefined} style={{ ...td, background: fondJour(d), color: 'var(--red-600)', fontWeight: 700 }}>
                      {v.length || ''}
                    </td>
                  );
                })}
                <td colSpan={3} style={td} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="prev-legende" style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>
        <strong style={{ color: 'var(--red-600)' }}>Abs</strong> absence validée ·{' '}
        <strong style={{ color: 'var(--amber-600)' }}>Dem</strong> absence demandée · * = titulaire sous dérogation · f = en formation, en doublon ·{' '}
        {projet ? 'cliquez une case pour savoir pourquoi cette personne a été choisie.' : 'le mois ne comporte encore que les absences.'}
      </p>

      {choisie && sel.length > 0 && (
        <div className="prev-ecran" style={{ marginTop: 10, padding: '10px 12px', border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13, flex: 1 }}>
              Pourquoi {selPers ? nomDe(selPers) : choisie.orgId} le {choisie.date.slice(8)}/{choisie.date.slice(5, 7)}
            </strong>
            <Bouton taille="sm" type="button" onClick={() => onBasculer(sel.map(cle))}>
              {sel.every((x) => ecartees.has(cle(x))) ? 'Reprendre' : 'Écarter'}
            </Bouton>
            <Bouton taille="sm" type="button" onClick={() => setChoisie(null)}>Fermer</Bouton>
          </div>
          {sel.map((x) => (
            <div key={cle(x)} style={{ marginTop: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{x.posteLibelle}{x.derogation ? ' — sous dérogation' : ''}{x.formation ? ' — en formation (doublon)' : ''}</div>
              <ul style={{ margin: '3px 0 0', paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.5 }}>
                {(x.raisons || ['(raison non transmise)']).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          ))}
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>
            Une remarque sur ce choix ? Dites-la : c&apos;est ainsi qu&apos;on ajuste les règles.
          </p>
        </div>
      )}
    </div>
  );
}
